import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatAuDate } from "@/lib/date";
import { toast } from "sonner";
import { Mail, Send, Calendar as CalendarIcon, Trash2, Pencil, FileText, Paperclip, Upload as UploadIcon } from "lucide-react";

export function BroadcastView({ alloc, eventId, onSent }) {
  const [channel, setChannel] = useState("sms");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [days, setDays] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState("now"); // "now" | "schedule"
  const [sendAtLocal, setSendAtLocal] = useState(""); // datetime-local string
  const [notifyMe, setNotifyMe] = useState(true);
  const [scheduled, setScheduled] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [attachment, setAttachment] = useState(null); // { id, filename, size, content_type, thumbnail_url } | null
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  // "Send to absolutely everyone" — when true we bypass every filter on the
  // backend AND visually highlight every filter pill so the admin can see
  // exactly who's in scope.
  const [everyone, setEveryone] = useState(false);
  // Live "N of M" scope banner. `totalReach` is refreshed on every channel
  // switch (SMS vs Email dedupe key differs, so the M can differ too). `reach`
  // is a debounced N based on current filters.
  const [totalReach, setTotalReach] = useState(null);
  const [reach, setReach] = useState(null);
  const [reachLoading, setReachLoading] = useState(false);

  const allDays = (alloc?.days || []).map((d) => d.date);
  const allSessions = Array.from(new Set((alloc?.days || []).flatMap((d) => (d.sessions || []).map((s) => s.name))));
  const allRoles = Array.from(new Set((alloc?.days || []).flatMap((d) => (d.sessions || []).flatMap((s) => s.roles || []))));

  const wordCount = message.trim() ? message.trim().split(/\s+/).length : 0;
  const wordLimit = channel === "sms" ? 100 : 200;
  const overLimit = wordCount > wordLimit;

  function toggle(list, setList, val) {
    // Any manual filter tweak turns the "everyone" override off so the admin
    // sees consistent state.
    setEveryone(false);
    setList(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);
  }

  function buildPayload(extra = {}) {
    return { channel, message, subject, days, sessions, roles, statuses, everyone, attachment_id: attachment?.id || "", ...extra };
  }

  // Pydantic 422 returns `detail` as an array of `{type,loc,msg,...}` objects
  // which React can't render — always coerce to a plain string before toasting.
  function apiErrMsg(err, fallback) {
    const d = err?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d) && d.length) {
      return d.map((x) => (typeof x === "string" ? x : (x?.msg || JSON.stringify(x)))).join("; ");
    }
    return err?.message || fallback || "Something went wrong";
  }

  async function fetchThumbnail(id) {
    try {
      const res = await api.get(`/admin/events/${eventId}/broadcast-attachments/${id}/thumbnail`, { responseType: "blob" });
      return URL.createObjectURL(res.data);
    } catch (e) { return ""; }
  }

  async function uploadAttachment(file) {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Only PDF files are supported"); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error(`PDF is ${(file.size/1024/1024).toFixed(1)} MB — 3 MB max`); return; }
    setUploadingAttachment(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/events/${eventId}/broadcast-attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const thumbnail_url = await fetchThumbnail(data.id);
      setAttachment({ ...data, thumbnail_url });
      toast.success(`Attached ${data.filename} (${(data.size/1024).toFixed(0)} KB)`);
    } catch (e) { toast.error(apiErrMsg(e, "Upload failed")); }
    finally { setUploadingAttachment(false); }
  }

  async function removeAttachment() {
    if (!attachment) return;
    const id = attachment.id;
    if (attachment.thumbnail_url) { try { URL.revokeObjectURL(attachment.thumbnail_url); } catch {} }
    setAttachment(null);
    try { await api.delete(`/admin/events/${eventId}/broadcast-attachments/${id}`); } catch (e) { /* ignore */ }
  }

  function sendToAbsolutelyEveryone() {
    // Toggle the override. When ON: every filter pill visually lights up AND
    // the backend receives `everyone: true` which bypasses filter clauses.
    setEveryone((v) => {
      const next = !v;
      if (next) {
        toast.info("Recipients now include every volunteer registered for this event — regardless of status, day, session or role.");
      }
      return next;
    });
  }

  async function runPreview() {
    try {
      const noScope = !everyone && !days.length && !sessions.length && !roles.length && !statuses.length;
      const { data } = await api.post(`/admin/events/${eventId}/bulk-preview`, buildPayload());
      setPreview({ ...data, no_scope: noScope });
    } catch (e) { toast.error(apiErrMsg(e, "Preview failed")); }
  }

  // Fetch M (the addressable total for this channel) whenever the channel
  // switches. Uses `everyone=true` to bypass every filter.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/admin/events/${eventId}/bulk-preview`, {
          channel, message: "", subject: "", days: [], sessions: [], roles: [], statuses: [], everyone: true, attachment_id: "",
        });
        if (!cancelled) setTotalReach(data?.count ?? 0);
      } catch { if (!cancelled) setTotalReach(0); }
    })();
    return () => { cancelled = true; };
  }, [eventId, channel]);

  // Fetch N (current filter reach) whenever a filter changes. Debounced 300ms
  // so rapid clicks don't spam the backend.
  useEffect(() => {
    const noScope = !everyone && !days.length && !sessions.length && !roles.length && !statuses.length;
    if (noScope) { setReach(0); setReachLoading(false); return; }
    setReachLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(`/admin/events/${eventId}/bulk-preview`, {
          channel, message: "", subject: "", days, sessions, roles, statuses, everyone, attachment_id: "",
        });
        setReach(data?.count ?? 0);
      } catch { setReach(null); }
      finally { setReachLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [eventId, channel, days, sessions, roles, statuses, everyone]);

  function localToIso(local) {
    if (!local) return "";
    // "YYYY-MM-DDTHH:MM" — treat as user's local time and convert to ISO/UTC
    const dt = new Date(local);
    return isNaN(dt.getTime()) ? "" : dt.toISOString();
  }

  const loadScheduled = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/events/${eventId}/scheduled-broadcasts`);
      setScheduled(data);
    } catch (e) { /* silent */ }
  }, [eventId]);
  useEffect(() => {
    loadScheduled();
    const t = setInterval(loadScheduled, 15000);
    return () => clearInterval(t);
  }, [loadScheduled]);

  function resetForm() {
    setMessage(""); setSubject(""); setPreview(null); setSendAtLocal(""); setEditingId(null); setMode("now");
    setNotifyMe(true);
    setDays([]); setSessions([]); setRoles([]); setStatuses([]);
    setAttachment(null);
  }

  async function send() {
    if (!message.trim()) return toast.error("Type a message");
    if (overLimit) return toast.error(`Message is over the ${wordLimit}-word limit`);
    if (mode === "schedule" && !sendAtLocal) return toast.error("Pick a date and time to schedule for");
    setSending(true);
    try {
      if (mode === "schedule") {
        const send_at = localToIso(sendAtLocal);
        if (!send_at) throw new Error("Invalid date/time");
        const payload = buildPayload({ send_at, notify_me: notifyMe });
        if (editingId) {
          await api.patch(`/admin/events/${eventId}/scheduled-broadcasts/${editingId}`, payload);
          toast.success("Scheduled broadcast updated");
        } else {
          await api.post(`/admin/events/${eventId}/scheduled-broadcasts`, payload);
          toast.success(`Scheduled to send at ${new Date(sendAtLocal).toLocaleString()}`);
        }
        resetForm();
        loadScheduled();
      } else {
        const p = preview?.count ?? "?";
        if (!window.confirm(`Send this ${channel.toUpperCase()} to ${p} recipient(s) right now?`)) { setSending(false); return; }
        const { data } = await api.post(`/admin/events/${eventId}/bulk-send`, buildPayload());
        toast.success(`${channel.toUpperCase()} broadcast: sent ${data.sent}/${data.total}${data.failed ? `, ${data.failed} failed` : ""}`);
        resetForm();
        onSent?.();
      }
    } catch (e) { toast.error(apiErrMsg(e, "Send failed")); }
    finally { setSending(false); }
  }

  async function cancelScheduled(id) {
    if (!window.confirm("Cancel this scheduled broadcast? It will not be sent.")) return;
    try {
      await api.delete(`/admin/events/${eventId}/scheduled-broadcasts/${id}`);
      toast.success("Cancelled");
      loadScheduled();
    } catch (e) { toast.error(apiErrMsg(e, "Cancel failed")); }
  }

  async function editScheduled(sched) {
    setMode("schedule");
    setEditingId(sched.id);
    setChannel(sched.channel);
    setSubject(sched.subject || "");
    setMessage(sched.message || "");
    setDays(sched.days || []);
    setSessions(sched.sessions || []);
    setRoles(sched.roles || []);
    setStatuses(sched.statuses || []);
    setNotifyMe(!!sched.notify_me);
    // Restore attachment if still present
    setAttachment(null);
    if (sched.attachment_id) {
      try {
        const { data } = await api.get(`/admin/events/${eventId}/broadcast-attachments`);
        const found = (data || []).find((a) => a.id === sched.attachment_id);
        if (found) {
          const thumbnail_url = await fetchThumbnail(found.id);
          setAttachment({ id: found.id, filename: found.filename, size: found.size, content_type: found.content_type, thumbnail_url });
        }
      } catch { /* silent */ }
    }
    // Convert ISO → local datetime-local input string
    try {
      const dt = new Date(sched.send_at);
      const pad = (n) => String(n).padStart(2, "0");
      const val = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
      setSendAtLocal(val);
    } catch { /* ignore */ }
    setPreview(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // Minimum datetime-local value = now + ~1 minute
  const nowPlus = () => {
    const d = new Date(Date.now() + 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const upcoming = scheduled.filter((s) => s.status === "scheduled");
  const past = scheduled.filter((s) => s.status !== "scheduled");

  return (
    <div className="space-y-6">
      <div className="border border-black/10 bg-white p-6" data-testid="broadcast-view">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#002FA7]">Bulk broadcast</div>
            <h2 className="mt-1 font-display text-2xl font-black tracking-tighter">
              {editingId ? "Edit scheduled broadcast" : "Message a filtered group of volunteers"}
            </h2>
          </div>
        </div>

        {/* "Send to absolutely everyone" — above the filters. Toggling it lights
            up every filter pill visually AND flags the payload so the backend
            bypasses filter clauses entirely (fixes the recurring 'select all
            filters, only see a couple of people' bug). */}
        <div className={`mt-4 border-2 p-3 ${everyone ? "border-[#002FA7] bg-[#002FA7]/[0.08]" : "border-[#002FA7]/40 bg-white"}`}>
          <button
            onClick={sendToAbsolutelyEveryone}
            data-testid="broadcast-send-to-everyone-btn"
            className={`inline-flex items-center gap-1.5 border-2 px-4 py-2 text-xs font-black tracking-widest uppercase ${everyone ? "border-[#002FA7] bg-[#002FA7] text-white hover:bg-white hover:text-[#002FA7]" : "border-[#002FA7] bg-white text-[#002FA7] hover:bg-[#002FA7] hover:text-white"}`}
          >
            <Send className="h-3.5 w-3.5" /> {everyone ? "✓ Sending to absolutely everyone" : "Send to absolutely everyone"}
          </button>
          <p className="mt-2 text-[11px] text-zinc-600">
            {everyone
              ? "Every volunteer registered for this event will receive this message — unallocated, pending, confirmed, everyone. Filters below are ignored while this is on."
              : "Click to include every volunteer regardless of allocation status, day, session or role. All filters below will light up to show the full scope."}
          </p>
        </div>

        {/* Channel picker moved to the LEFT — next to the filters so it's obvious which channel is active */}
        <div className="mt-4 border border-black/15 bg-[#F9F9FB] p-3" data-testid="broadcast-channel-block">
          <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">Channel</div>
          <div className="mt-2 inline-flex border border-black/20">
            <button onClick={() => setChannel("sms")} data-testid="broadcast-channel-sms" className={`px-4 py-2 text-xs font-semibold ${channel === "sms" ? "bg-black text-white" : "bg-white text-black hover:bg-zinc-100"}`}>SMS · 100 words</button>
            <button onClick={() => setChannel("email")} data-testid="broadcast-channel-email" className={`px-4 py-2 text-xs font-semibold ${channel === "email" ? "bg-black text-white" : "bg-white text-black hover:bg-zinc-100"}`}>Email · 200 words</button>
          </div>
        </div>

        {/* Send now / Schedule toggle */}
        <div className="mt-5 inline-flex border border-black/15" data-testid="broadcast-mode-toggle">
          <button onClick={() => setMode("now")} data-testid="broadcast-mode-now" className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold ${mode === "now" ? "bg-[#002FA7] text-white" : "bg-white text-black hover:bg-zinc-100"}`}>
            <Send className="h-3.5 w-3.5" /> Send now
          </button>
          <button onClick={() => setMode("schedule")} data-testid="broadcast-mode-schedule" className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold ${mode === "schedule" ? "bg-[#002FA7] text-white" : "bg-white text-black hover:bg-zinc-100"}`}>
            <CalendarIcon className="h-3.5 w-3.5" /> Schedule for later
          </button>
        </div>

        {mode === "schedule" ? (
          <div className="mt-4 border border-[#002FA7]/30 bg-[#002FA7]/[0.04] p-4" data-testid="broadcast-schedule-block">
            <label className="mb-1 block text-xs font-medium uppercase tracking-widest text-[#002FA7]">Send at (your local time)</label>
            <input type="datetime-local" min={nowPlus()} value={sendAtLocal} onChange={(e) => setSendAtLocal(e.target.value)}
              className="border-2 border-[#002FA7] bg-white px-3 py-2 text-sm font-semibold text-[#002FA7]" data-testid="broadcast-send-at" />
            {sendAtLocal ? (
              <div className="mt-1 text-[11px] text-zinc-600">Scheduled for <span className="font-mono">{new Date(sendAtLocal).toLocaleString()}</span></div>
            ) : null}
            <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs text-[#002FA7]">
              <input
                type="checkbox"
                checked={notifyMe}
                onChange={(e) => setNotifyMe(e.target.checked)}
                className="h-4 w-4 accent-[#002FA7]"
                data-testid="broadcast-notify-me"
              />
              <span><b>Notify me by email</b> when this broadcast fires (delivered / failed summary).</span>
            </label>
          </div>
        ) : null}

        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <div>
            <FilterGroup title="Days" all={allDays} value={days} onToggle={(v) => toggle(days, setDays, v)} testIdPrefix="broadcast-day" formatLabel={formatAuDate} forceAllSelected={everyone} />
            <div className="mt-4"><FilterGroup title="Event elements" all={allSessions} value={sessions} onToggle={(v) => toggle(sessions, setSessions, v)} testIdPrefix="broadcast-session" forceAllSelected={everyone} /></div>
            <div className="mt-4"><FilterGroup title="Roles" all={allRoles} value={roles} onToggle={(v) => toggle(roles, setRoles, v)} testIdPrefix="broadcast-role" forceAllSelected={everyone} /></div>
            <div className="mt-4"><FilterGroup title="Status" all={["pending", "confirmed", "declined", "unassigned"]} value={statuses} onToggle={(v) => toggle(statuses, setStatuses, v)} testIdPrefix="broadcast-status" forceAllSelected={everyone} /></div>
            <button onClick={runPreview} data-testid="broadcast-preview-btn" className="mt-5 inline-flex items-center gap-1.5 border border-black bg-black px-3 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black">
              Preview recipients
            </button>
            {preview ? (
              <div className="mt-4 border border-black/10 bg-[#F9F9FB] p-4">
                <div className="text-xs uppercase tracking-widest text-[#002FA7]" data-testid="broadcast-preview-count">{preview.count} recipient{preview.count !== 1 ? "s" : ""}</div>
                {preview.recipients?.length ? (
                  <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-xs">
                    {preview.recipients.slice(0, 30).map((r) => (
                      <li key={r.id} className="flex gap-2 border-b border-black/5 py-1"><span className="font-semibold">{r.name}</span><span className="font-mono text-zinc-500">{channel === "sms" ? r.mobile : r.email}</span></li>
                    ))}
                  </ul>
                ) : preview.no_scope ? (
                  <p className="mt-2 text-xs italic text-zinc-600" data-testid="broadcast-preview-no-scope">Pick at least one filter above (days, event elements, roles or status) — or click <b>Send to absolutely everyone</b> — before previewing.</p>
                ) : <p className="mt-2 text-xs italic text-zinc-500">No matching recipients.</p>}
              </div>
            ) : null}
          </div>
          <div>
            <ScopeBanner
              channel={channel}
              reach={reach}
              totalReach={totalReach}
              loading={reachLoading}
              everyone={everyone}
              hasFilters={Boolean(days.length || sessions.length || roles.length || statuses.length)}
            />
            {channel === "email" ? (
              <label className="mt-4 block">
                <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Subject</span>
                <input value={subject} onChange={(e) => setSubject(e.target.value.slice(0, 200))} className="w-full border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#002FA7]" data-testid="broadcast-subject" placeholder="Optional; defaults to the event name" />
              </label>
            ) : null}
            <label className="mt-3 block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Message ({channel === "sms" ? "SMS — 100 word max" : "Email — 200 word max"})</span>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} className="w-full min-h-[280px] border border-black/15 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[#002FA7]" data-testid="broadcast-message" placeholder={`Hi {name}, ...\n\nPlaceholder: {name}, {event}`} />
            </label>
            <div className={`mt-1 text-right text-xs ${overLimit ? "text-red-700" : "text-zinc-500"}`} data-testid="broadcast-wordcount">{wordCount}/{wordLimit} words</div>

            {channel === "email" ? (
              <div className="mt-4 border border-black/15 bg-[#F9F9FB] p-3" data-testid="broadcast-attachment-block">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-zinc-600">
                  <Paperclip className="h-3.5 w-3.5" /> Attach a PDF (optional · 3 MB max)
                </div>
                <p className="mt-1 text-[11px] text-zinc-500">Maps, event info, run sheets — attached to every email in this broadcast. Recipients see it as a normal PDF attachment.</p>
                {attachment ? (
                  <div className="mt-3 border border-emerald-300 bg-emerald-50 p-3" data-testid="broadcast-attachment-current">
                    <div className="flex flex-wrap items-start gap-3">
                      {attachment.thumbnail_url ? (
                        <img
                          src={attachment.thumbnail_url}
                          alt={`${attachment.filename} preview`}
                          data-testid="broadcast-attachment-preview"
                          className="h-32 w-24 border border-black/20 bg-white object-cover object-top shadow-[2px_2px_0_0_rgba(0,0,0,0.08)]"
                        />
                      ) : (
                        <div className="flex h-32 w-24 items-center justify-center border border-black/10 bg-white" data-testid="broadcast-attachment-preview-placeholder">
                          <FileText className="h-6 w-6 text-zinc-400" />
                        </div>
                      )}
                      <div className="flex flex-1 flex-col gap-1 min-w-[180px]">
                        <div className="text-[10px] font-medium uppercase tracking-widest text-emerald-800">Attached · first-page preview</div>
                        <div className="font-semibold text-emerald-900 break-all" data-testid="broadcast-attachment-filename">{attachment.filename}</div>
                        <div className="font-mono text-[10px] text-emerald-800">{(attachment.size / 1024).toFixed(0)} KB · PDF</div>
                        <button onClick={removeAttachment} data-testid="broadcast-attachment-remove" className="mt-1 inline-flex w-fit items-center gap-1 border border-red-300 bg-white px-2 py-1 text-[11px] text-red-700 hover:bg-red-600 hover:text-white">
                          <Trash2 className="h-3 w-3" /> Remove attachment
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <label className="mt-3 inline-flex cursor-pointer items-center gap-1.5 border border-black bg-black px-3 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black" data-testid="broadcast-attachment-upload-btn">
                    <UploadIcon className="h-3.5 w-3.5" />
                    {uploadingAttachment ? "Uploading…" : "Choose PDF"}
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadAttachment(f);
                        e.target.value = "";
                      }}
                      disabled={uploadingAttachment}
                      className="hidden"
                      data-testid="broadcast-attachment-input"
                    />
                  </label>
                )}
              </div>
            ) : null}
            <div className="mt-4 flex gap-2">
              <button onClick={send} disabled={sending || overLimit || !message.trim() || (mode === "schedule" && !sendAtLocal)}
                data-testid="broadcast-send-btn"
                className="inline-flex flex-1 items-center justify-center gap-2 border border-black bg-black px-5 py-3 text-sm font-semibold text-white hover:bg-white hover:text-black disabled:opacity-40">
                {mode === "schedule" ? <CalendarIcon className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                {sending ? "Working…" : mode === "schedule"
                  ? (editingId ? "UPDATE SCHEDULED" : `SCHEDULE ${channel.toUpperCase()}`)
                  : `SEND ${channel.toUpperCase()}`}
              </button>
              {editingId ? (
                <button onClick={resetForm} data-testid="broadcast-cancel-edit-btn" className="border border-black/20 px-4 py-3 text-xs font-medium hover:bg-black hover:text-white">Cancel edit</button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Scheduled queue */}
      <div className="border border-black/10 bg-white p-6" data-testid="scheduled-broadcasts-block">
        <div className="text-xs uppercase tracking-widest text-[#002FA7]">Scheduled queue</div>
        <h3 className="mt-1 font-display text-xl font-black tracking-tighter">Upcoming and past broadcasts</h3>
        {upcoming.length === 0 && past.length === 0 ? (
          <p className="mt-3 text-xs italic text-zinc-500">No scheduled broadcasts yet.</p>
        ) : null}
        {upcoming.length > 0 ? (
          <div className="mt-4">
            <div className="mb-2 text-[10px] font-medium uppercase tracking-widest text-[#002FA7]">Upcoming</div>
            <ul className="divide-y divide-black/5 border border-black/10">
              {upcoming.map((s) => (
                <li key={s.id} data-testid={`scheduled-row-${s.id}`} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                  <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest ${s.channel === "sms" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-blue-300 bg-blue-50 text-blue-800"}`}>{s.channel}</span>
                  <span className="font-mono text-xs" data-testid={`scheduled-send-at-${s.id}`}>{new Date(s.send_at).toLocaleString()}</span>
                  {s.notify_me ? <span className="inline-flex items-center gap-1 border border-[#002FA7]/40 bg-[#002FA7]/[0.06] px-1.5 py-0.5 text-[10px] font-medium text-[#002FA7]" data-testid={`scheduled-notify-badge-${s.id}`}><Mail className="h-3 w-3" /> notify me</span> : null}
                  {s.attachment_id ? <span className="inline-flex items-center gap-1 border border-amber-400 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800" data-testid={`scheduled-attachment-badge-${s.id}`}><Paperclip className="h-3 w-3" /> PDF</span> : null}
                  <span className="truncate max-w-[220px] text-xs text-zinc-600">{s.message.slice(0, 60)}{s.message.length > 60 ? "…" : ""}</span>
                  <div className="ml-auto flex items-center gap-2">
                    <button onClick={() => editScheduled(s)} data-testid={`scheduled-edit-${s.id}`} className="inline-flex items-center gap-1 border border-black/20 bg-white px-2 py-1 text-[11px] hover:bg-black hover:text-white"><Pencil className="h-3 w-3" /> Edit</button>
                    <button onClick={() => cancelScheduled(s.id)} data-testid={`scheduled-cancel-${s.id}`} className="inline-flex items-center gap-1 border border-red-300 bg-white px-2 py-1 text-[11px] text-red-700 hover:bg-red-600 hover:text-white"><Trash2 className="h-3 w-3" /> Cancel</button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {past.length > 0 ? (
          <div className="mt-4">
            <div className="mb-2 text-[10px] font-medium uppercase tracking-widest text-zinc-500">History</div>
            <ul className="divide-y divide-black/5 border border-black/10">
              {past.map((s) => (
                <li key={s.id} data-testid={`scheduled-history-${s.id}`} className="flex flex-wrap items-center gap-3 px-3 py-2 text-xs">
                  <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest ${s.status === "sent" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : s.status === "cancelled" ? "border-zinc-300 bg-zinc-50 text-zinc-700" : "border-red-300 bg-red-50 text-red-800"}`}>{s.status}</span>
                  <span className="font-mono">{s.channel}</span>
                  <span className="font-mono">{new Date(s.send_at).toLocaleString()}</span>
                  <span className="truncate max-w-[260px] text-zinc-600">{s.message.slice(0, 60)}{s.message.length > 60 ? "…" : ""}</span>
                  {s.result ? <span className="ml-auto font-mono text-[10px] text-zinc-500">sent {s.result.sent}/{s.result.total}{s.result.failed ? ` · ${s.result.failed} failed` : ""}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FilterGroup({ title, all, value, onToggle, testIdPrefix, formatLabel, forceAllSelected }) {
  const summary = forceAllSelected
    ? `all ${all.length} — everyone override`
    : (value.length ? `${value.length} selected` : "any");
  return (
    <div>
      <div className="mb-1 text-xs font-medium uppercase tracking-wider text-zinc-500">{title} ({summary})</div>
      <div className="flex flex-wrap gap-1.5">
        {all.length === 0 ? <span className="text-xs italic text-zinc-400">None available</span> : all.map((v) => {
          const isSelected = forceAllSelected || value.includes(v);
          return (
            <button key={v} onClick={() => onToggle(v)} data-testid={`${testIdPrefix}-${v.replace(/\s+/g, "")}`}
              className={`border px-2 py-1 text-xs ${isSelected ? "border-black bg-black text-white" : "border-black/15 bg-white hover:border-black"}`}>
              {formatLabel ? formatLabel(v) : v}
            </button>
          );
        })}
      </div>
    </div>
  );
}


function ScopeBanner({ channel, reach, totalReach, loading, everyone, hasFilters }) {
  // Three visual states:
  //   • "no scope" — no filter + no everyone override → red/amber warning
  //   • "everyone" — full override → deep IKB blue "all N" chip
  //   • "filtered" — some filter selected → neutral blue "N of M" chip
  const noScope = !everyone && !hasFilters;
  const channelLabel = channel === "sms" ? "SMS" : "email";
  const target = channel === "sms" ? "mobile numbers" : "email addresses";
  const total = totalReach ?? 0;
  const n = reach ?? 0;

  if (noScope) {
    return (
      <div data-testid="broadcast-scope-banner" data-scope="none" className="mb-4 flex items-start gap-3 border border-amber-400 bg-amber-50 px-4 py-3 text-xs text-amber-900">
        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white font-black">!</span>
        <div className="min-w-0">
          <div className="text-[11px] font-black uppercase tracking-widest">No recipients selected yet</div>
          <p className="mt-0.5 leading-snug">Pick at least one filter above — days, event elements, roles or status — or click <b>Send to absolutely everyone</b> to bypass every filter. This event has <b data-testid="broadcast-scope-total">{total}</b> unique {target} on file.</p>
        </div>
      </div>
    );
  }

  const pct = total > 0 ? Math.round((n / total) * 100) : 0;
  const isEveryone = everyone || (n === total && total > 0);
  return (
    <div data-testid="broadcast-scope-banner" data-scope={isEveryone ? "everyone" : "filtered"} className={`mb-4 border px-4 py-3 ${isEveryone ? "border-[#002FA7] bg-[#002FA7]/[0.06]" : "border-[#002FA7]/30 bg-blue-50"}`}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-black uppercase tracking-widest text-[#002FA7]">You're about to message</div>
          <div className="mt-0.5 text-sm text-zinc-800">
            <span className="font-display text-2xl font-black tracking-tighter text-[#002FA7]" data-testid="broadcast-scope-n">{loading ? "…" : n}</span>
            <span className="mx-1 text-zinc-500">of</span>
            <span className="font-display text-xl font-black tracking-tighter text-zinc-700" data-testid="broadcast-scope-m">{total}</span>
            <span className="ml-1.5 text-zinc-600">volunteer{total === 1 ? "" : "s"} via {channelLabel}</span>
          </div>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${isEveryone ? "bg-[#002FA7] text-white" : "bg-white text-[#002FA7] border border-[#002FA7]/30"}`}>
          {isEveryone ? "Everyone" : `${pct}%`}
        </span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden bg-white/60">
        <div
          className={`h-full ${isEveryone ? "bg-[#002FA7]" : "bg-[#002FA7]/70"}`}
          style={{ width: total > 0 ? `${Math.min(100, pct)}%` : "0%" }}
          data-testid="broadcast-scope-bar"
        />
      </div>
    </div>
  );
}
